import { useVadCore } from "@/shared/lib/vad/model/use-vad-core";
import { float32ArrayToWav } from "@/shared/lib/vad/lib/vad-utils";
import { AgentConnectionManager } from "@/entities/realtime-agent/model/use-agent-connection";
import { useRealtimeAgentStore } from "@/shared/store/realtime-agent";
import i18n from "@/shared/lib/i18n";
import { useCallback, useEffect, useRef } from "react";
import { LanguageCode } from "@/entities/realtime-agent/types/websocket-messages";
import { useVadStore } from "@/shared/store/vad";
import { useAvatarStore } from "@/shared/store/avatar-store";
import { useMediaStore } from "@/shared/store/voice-chat";
import { generateUUID } from "@/shared/utils/uuid";
import { startLatestTurn } from "@/entities/realtime-agent/model/turn-lifecycle";
import { latestUserActionGuard } from "@/shared/lib/latest-user-action-guard";
import { createIdleMotionScheduler } from "./idle-motion-policy";

// ============================================
// VAD Misfire 복구 설정
// ============================================
const VAD_RECOVERY_CONFIG = {
  MISFIRE_THRESHOLD: 10,        // 10회 연속 misfire 시 복구 시도
  RECOVERY_DELAY_MS: 500,       // 500ms 대기 후 재시작 (AudioContext 정리 시간)
  RESET_DELAY_MS: 3000,         // 3초간 misfire 없으면 카운터 리셋
  MAX_RESTART_ATTEMPTS: 3,      // 10초 내 최대 3회 재시작
  RESTART_WINDOW_MS: 10000,     // 재시작 시도 윈도우 (10초)
} as const;

export const useVoiceChatVAD = () => {
  // 🌐 언어 동기화 - i18n 이벤트 직접 구독 (React 리렌더링 주기와 무관하게 즉시 반영)
  const currentLanguageRef = useRef(i18n.language as LanguageCode);

  // i18n languageChanged 이벤트 직접 구독 - 즉시 반영
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      currentLanguageRef.current = lng as LanguageCode;
    };

    currentLanguageRef.current = i18n.language as LanguageCode;
    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  const setCurrentAnimation = useAvatarStore(state => state.setCurrentAnimation);
  const isMuted = useMediaStore(state => state.isMuted);
  const isMutedRef = useRef(isMuted);
  const isUserSpeakingRef = useRef(false);
  const idleMotionSchedulerRef = useRef<
    ReturnType<typeof createIdleMotionScheduler> | null
  >(null);

  // ============================================
  // VAD Misfire 복구 로직을 위한 refs
  // ============================================
  const misfireCountRef = useRef(0);
  const isRecoveringRef = useRef(false);
  const recoveryTimerRef = useRef<NodeJS.Timeout | null>(null);
  const resetTimerRef = useRef<NodeJS.Timeout | null>(null);
  const restartAttemptsRef = useRef<number[]>([]);
  const isMountedRef = useRef(true);
  const vadInstanceRef = useRef<any>(null);

  // vadStatus를 ref로 동기화 (setTimeout 내에서 안전하게 접근)
  const vadStatusRef = useRef(useVadStore.getState().vadStatus);
  useEffect(() => {
    const unsubscribe = useVadStore.subscribe(
      (state) => state.vadStatus,
      (status) => {
        vadStatusRef.current = status;
      }
    );
    return () => unsubscribe();
  }, []);

  // ============================================
  // 무음 아이들 모션: listening 상태가 일정 시간 유지되면 대기(toss) 모션으로 전환
  // toss 시나리오에는 액션 모션(펀치·발차기)이 셔플로 섞여 있어 무음 구간이 단조롭지 않음
  // ============================================
  const IDLE_MOTION_DELAY_MS = 12000;

  useEffect(() => {
    const scheduler = createIdleMotionScheduler({
      delayMs: IDLE_MOTION_DELAY_MS,
      getCurrentAnimation: () => useAvatarStore.getState().currentAnimation,
      isUserSpeaking: () => isUserSpeakingRef.current,
      enterIdleMotion: () => setCurrentAnimation('toss'),
      scheduleTimer: (callback, delayMs) => setTimeout(callback, delayMs),
      cancelTimer: (timer) => clearTimeout(timer),
    });
    idleMotionSchedulerRef.current = scheduler;

    const unsubscribe = useAvatarStore.subscribe(
      (state) => state.currentAnimation,
      (anim) => {
        if (anim === 'listening') {
          scheduler.schedule();
        } else {
          scheduler.cancel();
        }
      }
    );

    return () => {
      scheduler.cancel();
      if (idleMotionSchedulerRef.current === scheduler) {
        idleMotionSchedulerRef.current = null;
      }
      unsubscribe();
    };
  }, [setCurrentAnimation]);

  useEffect(() => {
    isMutedRef.current = isMuted;
    if (isMuted) {
      isUserSpeakingRef.current = false;
      idleMotionSchedulerRef.current?.schedule();
    }
  }, [isMuted]);

  // 사용자가 말을 시작하면 아이들(toss) 중이라도 듣기 모션으로 복귀
  const onSpeechStart = useCallback(() => {
    if (isMutedRef.current) {
      isUserSpeakingRef.current = false;
      return;
    }

    isUserSpeakingRef.current = true;
    setCurrentAnimation('listening');
    idleMotionSchedulerRef.current?.schedule();
  }, [setCurrentAnimation]);

  const onSpeechEnd = useCallback((audio: Float32Array) => {
    isUserSpeakingRef.current = false;
    idleMotionSchedulerRef.current?.schedule();
    const speechRequestGeneration = latestUserActionGuard.begin();
    const audioDuration = audio.length / 16000;
    let audioMax = 0;
    let sumSquares = 0;
    for (let i = 0; i < audio.length; i++) {
      const absVal = Math.abs(audio[i]);
      if (absVal > audioMax) audioMax = absVal;
      sumSquares += audio[i] * audio[i];
    }
    const audioRms = Math.sqrt(sumSquares / audio.length);

    // 오디오가 너무 짧거나 볼륨이 너무 낮으면 경고
    if (audioDuration < 0.5) {
      console.warn('⚠️ VAD: 오디오가 너무 짧습니다 (', audioDuration.toFixed(2), '초)');
    }
    if (audioRms < 0.01) {
      console.warn('⚠️ VAD: 오디오 볼륨이 너무 낮습니다 (RMS:', audioRms.toFixed(4), ')');
    }

    const wavBlob = float32ArrayToWav(audio);

    const reader = new FileReader();

    reader.onload = () => {
      if (!latestUserActionGuard.isCurrent(speechRequestGeneration)) {
        return;
      }

      const arrayBuffer = reader.result as ArrayBuffer;
      const uint8Array = new Uint8Array(arrayBuffer);

      // 큰 배열을 안전하게 처리하기 위해 청크 단위로 변환
      let binaryString = '';
      const chunkSize = 8192;
      for (let i = 0; i < uint8Array.length; i += chunkSize) {
        const chunk = uint8Array.subarray(i, i + chunkSize);
        binaryString += String.fromCharCode.apply(null, Array.from(chunk));
      }
      const audioBase64 = btoa(binaryString);

      const api = AgentConnectionManager.getAPI();
      const store = useRealtimeAgentStore.getState();
      const newTurnId = generateUUID();

      const sent = startLatestTurn({
        state: store,
        getCurrentTurnId: () => useRealtimeAgentStore.getState().currentTurnId,
        turnId: newTurnId,
        sendInterrupt: (turnId) => api?.isConnected
          ? api.sendInterrupt(turnId)
          : false,
        sendMessage: (turnId) => api?.isConnected
          ? api.sendUserMessage({
              turnId,
              mode: "voice",
              data: audioBase64,
              language: currentLanguageRef.current,
            })
          : false,
      });
      if (!sent) {
        console.warn('⚠️ VAD: WebSocket 전송 실패 - 로컬 턴을 정리했습니다.');
        return;
      }

      // 응답 대기 중에는 대기(toss)가 아닌 생각 중(processing) 모션 재생
      // 모든 아바타 설정에 processing 시나리오가 존재함을 확인함 (2026-08-20)
      setCurrentAnimation('processing');
    };

    reader.readAsArrayBuffer(wavBlob);
  }, [setCurrentAnimation]);

  // ============================================
  // VAD Misfire 복구 콜백
  // ============================================
  const onVADMisfire = useCallback(() => {
    isUserSpeakingRef.current = false;
    idleMotionSchedulerRef.current?.schedule();
    if (isRecoveringRef.current) return;

    misfireCountRef.current++;

    // 기존 리셋 타이머 정리
    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }

    // 임계값 미만: 3초 후 카운터 자동 리셋
    if (misfireCountRef.current < VAD_RECOVERY_CONFIG.MISFIRE_THRESHOLD) {
      resetTimerRef.current = setTimeout(() => {
        misfireCountRef.current = 0;
        resetTimerRef.current = null;
      }, VAD_RECOVERY_CONFIG.RESET_DELAY_MS);
      return;
    }

    // ============================================
    // 임계값 도달: 복구 시작
    // ============================================
    const now = Date.now();

    // 무한 루프 방지: 10초 내 재시작 시도 횟수 확인
    const recentAttempts = restartAttemptsRef.current.filter(
      (timestamp) => now - timestamp < VAD_RECOVERY_CONFIG.RESTART_WINDOW_MS
    );

    if (recentAttempts.length >= VAD_RECOVERY_CONFIG.MAX_RESTART_ATTEMPTS) {
      console.error('❌ VAD: 과도한 재시작 시도 (10초 내 3회 초과) - 자동 복구 중단');
      misfireCountRef.current = 0;
      return;
    }

    // 재시작 시도 기록
    restartAttemptsRef.current.push(now);
    restartAttemptsRef.current = restartAttemptsRef.current.filter(
      (timestamp) => now - timestamp < VAD_RECOVERY_CONFIG.RESTART_WINDOW_MS
    );

    isRecoveringRef.current = true;

    // VAD 일시 중단
    if (vadInstanceRef.current) {
      vadInstanceRef.current.pause();
    }

    // 기존 복구 타이머 정리
    if (recoveryTimerRef.current) {
      clearTimeout(recoveryTimerRef.current);
    }

    // 500ms 후 복구 시도
    recoveryTimerRef.current = setTimeout(() => {
      if (!isMountedRef.current) {
        isRecoveringRef.current = false;
        recoveryTimerRef.current = null;
        return;
      }

      const currentVadStatus = vadStatusRef.current;
      const currentIsMuted = isMutedRef.current;

      // 복구 조건: listening 상태이고, mute 아닐 때만
      if (currentVadStatus === 'listening' && !currentIsMuted) {
        if (vadInstanceRef.current) {
          vadInstanceRef.current.start();
        }
      }

      // 복구 상태 리셋
      misfireCountRef.current = 0;
      isRecoveringRef.current = false;
      recoveryTimerRef.current = null;
    }, VAD_RECOVERY_CONFIG.RECOVERY_DELAY_MS);
  }, []);

  // useVadCore에서 vad 인스턴스도 받아옴
  const { start, pause, vad, startPitchMonitoring, stopPitchMonitoring, pitch } = useVadCore({
    callbacks: {
      onSpeechStart: onSpeechStart,
      onSpeechEnd: onSpeechEnd,
      onVADMisfire: onVADMisfire
    }
  });

  // vad 인스턴스를 ref에 동기화
  useEffect(() => {
    vadInstanceRef.current = vad;
  }, [vad]);

  //TODO: 오디오 종료 이벤트 수신
  //VAD 상태 변화를 subscribe하고 "paused" | "listening"으로 두고, listening이 들어오면 Start
  useEffect(() => {
    const unsubscribe = useVadStore.subscribe((state) => state.vadStatus, (status) => {
      if (status === 'listening' && !isMutedRef.current) {
        start();
        startPitchMonitoring();
      } else {
        pause();
        stopPitchMonitoring();
      }
    })
    return () => unsubscribe();
  }, [start, pause, startPitchMonitoring, stopPitchMonitoring])

  // isMuted 상태 변화 감지하여 VAD 토글링 TODO: 리팩토링
  useEffect(() => {
    const vadStatus = useVadStore.getState().vadStatus;

    if (!isMuted && (vadStatus === 'listening')) {
      start();
      startPitchMonitoring();
    }
    else if (isMuted && vadStatus === 'listening') {
      pause();
      stopPitchMonitoring();
    }
  }, [isMuted, start, pause, startPitchMonitoring, stopPitchMonitoring])

  // ============================================
  // 복구 취소 함수 (외부에서 호출 가능)
  // ============================================
  const cancelRecovery = useCallback(() => {
    if (recoveryTimerRef.current) {
      clearTimeout(recoveryTimerRef.current);
      recoveryTimerRef.current = null;
    }
    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }

    misfireCountRef.current = 0;
    isRecoveringRef.current = false;
  }, []);

  // ============================================
  // Cleanup: 컴포넌트 언마운트 시 타이머 정리
  // ============================================
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      isUserSpeakingRef.current = false;
      latestUserActionGuard.cancel();

      if (recoveryTimerRef.current) {
        clearTimeout(recoveryTimerRef.current);
        recoveryTimerRef.current = null;
      }
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
        resetTimerRef.current = null;
      }

      misfireCountRef.current = 0;
      isRecoveringRef.current = false;
      restartAttemptsRef.current = [];
    };
  }, []);

  return {
    start,
    pause,
    startPitchMonitoring,
    stopPitchMonitoring,
    pitch,
    cancelRecovery,
  };
};
